import React from "react";
import { useFormContext } from "react-hook-form";
import TextController from "../../form-fields/TextController";
import PhoneNumberController from "../../form-fields/PhoneNumberController";
import DateController from "../../form-fields/DateController";
import SelectController from "../../form-fields/SelectController";
import ComboBoxController from "../../form-fields/ComboboxController";
import { getStates } from "../../../lib/utils";
import config from "../../../config";
import { Card, CardContent, CardHeader } from "../../ui/card";
import federalAgencies from "../../../utils/federal_agencies.json";
import AddressAutocomplete from "../../form-fields/AddressAutocomplete";
import GoogleMapProvider from "../../dashboard/GoogleMapProvider";

export default function ContactComponent({ client }: { client: ClientType }) {
  const { watch, setValue } = useFormContext();

  // 🔥 Watch values for live updates
  const watchedAddress = watch("address");
  const watchedCity = watch("city");
  const watchedZip = watch("zipCode");

  return (
    <Card className="mb-8 shadow" id="contact-information">
      <CardHeader>
        <h4 className="text-lg font-semibold text-gray-700">
          Contact Information
        </h4>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {/* Email */}
          <div className="my-2">
            <TextController
              name="email"
              label="Email"
              placeholder="Enter email address"
              defaultValue={client?.email || ""}
              validation={{
                required: "Email is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Invalid email address.",
                },
              }}
              infoMessage="Please enter the client's email address."
            />
          </div>

          {/* Mobile Phone */}
          <div className="my-2">
            <PhoneNumberController
              name="mobilePhone"
              label="Mobile Phone"
              placeholder="Enter mobile phone"
              defaultValue={client?.mobilePhone || ""}
              infoMessage="Please enter the client's mobile number."
            />
          </div>

          {/* Federal Agency (Combobox) */}
          <div className="my-2">
            <ComboBoxController
              name="federalAgency"
              label="Federal Agency"
              options={federalAgencies}
              defaultValue={client?.federalAgency || ""}
              infoMessage="Select the federal agency."
            />
          </div>

          {/* Home Address */}
          <div className="col-span-2 my-4">
            <h5 className="text-md font-semibold text-gray-600 mb-5">
              Home Address
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <GoogleMapProvider apiKey={config.GOOGLE_MAPS_API_KEY!}>
                {(isLoaded) => (
                  <>
                    <AddressAutocomplete
                      name="address"
                      label="Street Address"
                      defaultValue={watchedAddress || client?.address || ""}
                      isLoaded={isLoaded}
                    />
                  </>
                )}
              </GoogleMapProvider>

              {/* 🔥 Ensure city field updates */}
              <TextController
                name="city"
                label="City"
                placeholder="Enter city"
                defaultValue={watchedCity || client?.city || ""}
                infoMessage="Please enter the city."
              />

              <SelectController
                name="stateProvince"
                label="State / Province"
                options={getStates()}
                defaultValue={client?.stateProvince || ""}
                infoMessage="Please select the state or province."
              />

              {/* 🔥 Ensure zip code field updates */}
              <TextController
                name="zipCode"
                label="Zip Code"
                placeholder="Enter zip code"
                defaultValue={watchedZip || client?.zipCode || ""}
                validation={{
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Invalid zip code format.",
                  },
                }}
                infoMessage="Please enter the zip code."
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
